import { useQuery } from "@tanstack/react-query";
import { useShows } from "@/context/ShowContext";
import { premiumApi } from "@/api/premium.api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { GlassCard } from "@/components/ui/GlassCard";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

type AllowedPhase = "ps" | "is" | "cs";

function getShowPhase(firstPerf: string | null, lastPerf: string | null): AllowedPhase | null {
  if (!firstPerf) return null;
  const today = new Date().toISOString().slice(0, 10);
  if (lastPerf && lastPerf < today) return "cs";
  if (firstPerf > today) return "ps";
  return "is";
}

function BlockPage({ icon, title, message, showUpgrade, showSetup }: {
  icon: string; title: string; message: string; showUpgrade?: boolean; showSetup?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <GlassCard>
        <div className="text-center py-8 max-w-sm mx-auto">
          <div className="text-4xl mb-4">{icon}</div>
          <h2 className="text-lg font-extrabold text-text-primary mb-2">{title}</h2>
          <p className="text-sm text-text-secondary mb-6">{message}</p>
          {showUpgrade && (
            <Button onClick={() => navigate("/premium/checkout")} className="w-full py-3">
              Upgrade to Premium
            </Button>
          )}
          {showSetup && (
            <Button onClick={() => navigate("/new-show-setup")} className="w-full py-3">
              Set Up a Show
            </Button>
          )}
          <button onClick={() => navigate("/dashboard")}
            className="text-xs text-text-muted hover:text-text-primary mt-3 block mx-auto transition-colors">
            Back to Dashboard
          </button>
        </div>
      </GlassCard>
    </div>
  );
}

export function PremiumRoute({ children, phases }: { children: React.ReactNode; phases?: AllowedPhase[] }) {
  const { selectedShowId, selectedShow } = useShows();

  const { data: status, isLoading } = useQuery({
    queryKey: ["premium-status", selectedShowId],
    queryFn: () => premiumApi.getStatus(selectedShowId!),
    enabled: !!selectedShowId,
    staleTime: 60_000,
  });

  if (!selectedShowId) {
    return <BlockPage icon="&#127917;" title="No Show Selected"
      message="You need to set up or select a show before accessing this page." showSetup />;
  }

  if (isLoading) return <LoadingSpinner size="lg" />;

  const phase = getShowPhase(selectedShow?.first_perf_date ?? null, selectedShow?.last_perf_date ?? null);

  if (phases && phase && !phases.includes(phase)) {
    const phaseLabels: Record<string, string> = { ps: "Pre-Season", is: "In-Season", cs: "Season Complete" };
    const allowed = phases.map(p => phaseLabels[p]).join(" or ");
    return <BlockPage icon="&#128274;" title="Not Available"
      message={`This page is for ${allowed} shows. Your current show is ${phaseLabels[phase!] ?? "unknown"}.`} />;
  }

  if (phase === "cs" && (!phases || phases.includes("cs"))) {
    return <>{children}</>;
  }

  if (status?.is_premium) return <>{children}</>;

  return <BlockPage icon="&#9733;" title="Premium Feature"
    message={`This feature requires Premium access for ${selectedShow?.event_title || "your show"}. Upgrade to unlock the full coaching experience.`}
    showUpgrade />;
}

export function ShowRequiredRoute({ children }: { children: React.ReactNode }) {
  const { selectedShowId, isLoading } = useShows();

  if (isLoading) return <LoadingSpinner size="lg" />;

  if (!selectedShowId) {
    return <BlockPage icon="&#127917;" title="No Show Selected"
      message="You need to set up or select a show before accessing this page." showSetup />;
  }

  return <>{children}</>;
}
